/**
 * The four statuses an analysis shows — Not run, Running, Completed, Failed —
 * shared by the Analyses list and an analysis's own page. Waiting counts as
 * Running (with its loader); a result the data cannot feed (blocked) counts
 * as Not run, its reason on hover.
 */

import { CircularProgress, Typography } from '@mui/material';
import type { AnalysisOverviewRow } from '../../schemas';
import {
	missingColumnsShort,
	startedAgoLabel,
} from './analysisStatusPresentation';

export type ListStatus = 'NOT_RUN' | 'RUNNING' | 'COMPLETED' | 'FAILED';

const LOOK: Record<ListStatus, { label: string; badge: string }> = {
	NOT_RUN: { label: 'Not run', badge: 'gray' },
	RUNNING: { label: 'Running', badge: 'info' },
	COMPLETED: { label: 'Completed', badge: 'clean' },
	FAILED: { label: 'Failed', badge: 'exception' },
};

/** Blocked wins over a stale completed/failed state, never over a live run. */
export function isBlocked(row: AnalysisOverviewRow): boolean {
	return (
		row.availability === 'BLOCKED' &&
		row.status !== 'RUNNING' &&
		row.status !== 'QUEUED'
	);
}

export function listStatus(row: AnalysisOverviewRow): ListStatus {
	if (row.status === 'RUNNING' || row.status === 'QUEUED') return 'RUNNING';
	if (isBlocked(row)) return 'NOT_RUN';
	if (row.status === 'COMPLETED') return 'COMPLETED';
	if (row.status === 'FAILED') return 'FAILED';
	return 'NOT_RUN';
}

export function ListStatusBadge({
	row,
	running = false,
}: {
	row: AnalysisOverviewRow;
	/** Show as running even before the server says so (a run just asked for). */
	running?: boolean;
}) {
	const status = running ? 'RUNNING' : listStatus(row);
	const { label, badge } = LOOK[status];
	const help =
		status === 'FAILED'
			? row.error
			: status === 'NOT_RUN' && isBlocked(row)
				? (missingColumnsShort(row.missingColumns) ?? row.blockReason)
				: status === 'RUNNING'
					? startedAgoLabel(row.startedAt)
					: null;
	return (
		<Typography
			component="span"
			variant="inherit"
			className={`bdg ${badge} nowrap`}
			title={help ?? undefined}
		>
			{status === 'RUNNING' && (
				<CircularProgress
					size={11}
					thickness={6}
					color="inherit"
					sx={{ mr: 0.75 }}
				/>
			)}
			{label}
		</Typography>
	);
}
